<template>
  <v-card ref="data-source-details">
    <v-card-title>
      <v-row no-gutters>
        <v-col>Data Source Details</v-col>
        <v-col class="text-right">
          <vue-to-print :content="() => this.$refs['data-source-details'].$el"
                        :document-title="dataSource.name + '-' + 'data-source-details'"
                        remove-after-print>
            <template v-slot:trigger>
              <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
            </template>
          </vue-to-print>
          <v-icon size="small" color="warning" v-tooltip="'Imported data source'"
                  v-if="dataSource.externalId && dataSource.importSource">
            mdi-account-alert-outline
          </v-icon>
          <v-icon size="small" color="success" v-tooltip="'Not imported'" v-else>
            mdi-earth
          </v-icon>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="6" class="text-subtitle-2 text-wrap">
          Saved at: {{formatTimestamp(dataSource.date_time)}} by {{dataSource.lastUpdatedBy}}
        </v-col>
        <v-col cols="2">
          <div v-if="hasAccessRight('data-landscaping-admin')">
            <v-icon size="small" class="me-2"
                    v-tooltip="'Next version'"
                    @click="internalFailure = undefined; this.$emit('next-version')"
                    :disabled="dataSource.version === currentVersion">mdi-arrow-up</v-icon>
            <v-icon size="small" class="me-2"
                    v-tooltip="'Previous version'"
                    @click="internalFailure = undefined; this.$emit('previous-version')"
                    :disabled="dataSource.version === 0">mdi-arrow-down</v-icon>
          </div>
        </v-col>
        <v-col cols="4">
          <div class="error text-subtitle-2 text-wrap" v-if="internalFailure || failure">{{internalFailure || failure}}</div>
        </v-col>
      </v-row>
    </v-card-title>
    <v-divider></v-divider>
    <v-sheet elevation="0" max-height="2000" class="overflow-y-auto">
      <v-card-text>
        <h2 :class="computeValuesChangedDeltaClass(delta, 'name')">{{dataSource.name}}</h2>
        <br/>
        <div v-if="dataSource.files?.length > 0">
          <h3>Attached documents</h3>
          <br/>
          <v-row>
            <v-col>
              <v-chip v-for="(file, i) in dataSource.files" :key="i"
                      :class="computeFilesAddedClass(delta, file) + ' me-2'"
                      color="primary" size="small" :label="true"
                      :disabled="dataSource.version !== currentVersion"
                      @click.stop="openFile(dataSource.id, file.filename, file.contentType)">
                <template v-slot:default>
                  {{ file.filename }}
                </template>
              </v-chip>
              <v-chip v-for="(removedFile, i) in computeFilesRemoved(delta)" :key="i"
                      :class="'delta-removed me-2'"
                      color="primary" size="small" :label="true"
                      :disabled="true">
                <template v-slot:default>
                  {{ removedFile.filename }}
                </template>
              </v-chip>
            </v-col>
          </v-row>
          <br/>
          <br/>
        </div>
        <h3>General information about the data source</h3>
        <v-data-table
            density="compact"
            width="200px"
            :items-per-page="0"
            :headers="[{align: 'start', key: 'label', width: '50%'}, {align: 'start', key: 'value', width: '50%'}]"
            :items="[
                          {label: 'Therapeutic area', value: dataSource.therapeuticArea, key: 'therapeuticArea'},
                          {label: 'Countries', value: dataSource.country, key: 'country'},
                          {label: 'Comment to countries', value: dataSource.countryComment, key: 'countryComment'},
                          {label: 'Regional coverage', value: dataSource.regionalCoverage, key: 'regionalCoverage'},
                          {label: 'Geographical set-up', value: dataSource.geographicalSetUp, key: 'geographicalSetUp'},
                          {label: 'Data source type', value: dataSource.dataSourceType, key: 'dataSourceType'},
                          {label: 'Retrospective or Prospective collection', value: dataSource.retrospectiveOrProspectiveCollection, key: 'retrospectiveOrProspectiveCollection'},
                          {label: 'Longitudinal data collection', value: dataSource.longitudinalDataCollection, key: 'longitudinalDataCollection'},
                          {label: 'Outreach performed', value: dataSource.outreachPerformed, key: 'outreachPerformed'}
                        ]">
          <template #headers></template>
          <template #bottom></template>
          <template #no-data></template>
          <template v-slot:[`item.value`]="{ item }">
            <div v-if="['Therapeutic area', 'Countries', 'Geographical set-up', 'Data source type'].includes(item.label)">
              <v-chip v-for="value in item.value" :key="value"
                      :class="computeValuesAddedClass(delta, item.key, value) + ' me-2'"
                      color="primary" size="small" :label="true">
                {{ value }}
              </v-chip>
              <v-chip v-for="(removedValue, i) in computeValuesRemoved(delta, item.key)" :key="i"
                      :class="'delta-removed me-2'"
                      color="primary" size="small" :label="true"
                      :disabled="true">
                <template v-slot:default>
                  {{ removedValue }}
                </template>
              </v-chip>
            </div>
            <div v-else-if="['Retrospective or Prospective collection', 'Longitudinal data collection', 'Outreach performed'].includes(item.label)">
              <v-chip :class="computeValuesChangedDeltaClass(delta, item.key)"
                      color="primary" size="small" :label="true">
                {{ item.value }}
              </v-chip>
            </div>
            <div v-else :class="computeValuesChangedDeltaClass(delta, item.key)">
              {{ item.value }}
            </div>
          </template>
        </v-data-table>
        <br/>
        <h3>Short description/Abstract</h3>
        <div v-for="abstract in dataSource?.shortDescriptionAbstract?.split('\n')" :key="abstract"
             :class="computeValuesChangedDeltaClass(delta, 'shortDescriptionAbstract')">
          <p>{{abstract || '&nbsp;'}}</p>
        </div>
        <br/>
        <h3>Publication information</h3>
        <v-data-table
            density="compact"
            width="200px"
            :items-per-page="0"
            :headers="[{align: 'start', key: 'label', width: '50%'}, {align: 'start', key: 'value', width: '50%'}]"
            :items="[
                            {label: 'Publications', value: dataSource.publication, key: 'publication'},
                            {label: 'Link to data source website', value: dataSource.linkToDataSourceWebsite, key: 'linkToDataSourceWebsite'},
                            {label: 'Other relevant websites', value: dataSource.otherRelevantWebsites, key: 'otherRelevantWebsites'}
                        ]">
          <template #headers></template>
          <template #bottom></template>
          <template #no-data></template>
          <template v-slot:[`item.value`]="{ item }">
            <div v-if="item.label==='Publications'"
                 :class="computeValuesChangedDeltaClass(delta, item.key)">
              <div v-for="publication in item.value?.split('\n')" :key="publication">
                <p>{{publication || '&nbsp;'}}</p>
              </div>
            </div>
            <div v-else :class="computeValuesChangedDeltaClass(delta, item.key)">
              {{ item.value }}
            </div>
          </template>
        </v-data-table>
        <br/>
        <div v-for="(dataSourceCharacteristics, i) in dataSource.dataSourceCharacteristics" :key="i">
          <h3 :class="computeSectionDeltaClass(delta, 'dataSourceCharacteristics', i)">
            {{dataSourceCharacteristics.title}}</h3>
          <v-data-table
              density="compact"
              :items-per-page="0"
              :headers="[{align: 'start', key: 'label', width: '50%'}, {align: 'start', key: 'value', width: '50%'}]"
              :items="dataSourceCharacteristics.items.map((data, index) => ({label: data.label, value: data.value, index: index}))">
            <template #headers></template>
            <template #bottom></template>
            <template #no-data></template>
            <template v-slot:[`item.label`]="{ item }">
              <div :class="computeLabelDeltaClass(delta, 'dataSourceCharacteristics', i, item.index)">
                {{ item.label }}
              </div>
            </template>
            <template v-slot:[`item.value`]="{ item }">
              <div :class="computeValueDeltaClass(delta, 'dataSourceCharacteristics', i, item.index)">
                {{ item.value }}
              </div>
            </template>
          </v-data-table>
          <v-data-table
              density="compact"
              :items-per-page="0"
              :headers="[{align: 'start', key: 'label', width: '50%'}, {align: 'start', key: 'value', width: '50%'}]"
              :items="computeLabelValuesRemoved(delta, 'dataSourceCharacteristics', i)">
            <template #headers></template>
            <template #bottom></template>
            <template #no-data></template>
            <template v-slot:[`item.label`]="{ item }">
              <div class="delta-removed-disabled text-disabled">
                {{ item.label }}
              </div>
            </template>
            <template v-slot:[`item.value`]="{ item }">
              <div class="delta-removed-disabled text-disabled">
                {{ item.value }}
              </div>
            </template>
          </v-data-table>
          <br/>
        </div>
        <div v-for="(removedSection, i) in computeSectionsRemoved(delta, 'dataSourceCharacteristics')" :key="i">
          <h3 class="delta-removed-disabled text-disabled">
            {{removedSection.title}}</h3>
          <v-data-table
              density="compact"
              :items-per-page="0"
              :headers="[{align: 'start', key: 'label', width: '50%'}, {align: 'start', key: 'value', width: '50%'}]"
              :items="removedSection.items.map(data => ({label: data.label, value: data.value}))">
            <template #headers></template>
            <template #bottom></template>
            <template #no-data></template>
            <template v-slot:[`item.label`]="{ item }">
              <div class="delta-removed-disabled text-disabled">
                {{ item.label }}
              </div>
            </template>
            <template v-slot:[`item.value`]="{ item }">
              <div class="delta-removed-disabled text-disabled">
                {{ item.value }}
              </div>
            </template>
          </v-data-table>
          <br/>
        </div>
        <div v-if="dataSource.recommendation?.length > 0">
          <h3>Recommendation</h3>
          <v-data-table
              density="compact"
              :items-per-page="0"
              :headers="[{align: 'start', key: 'label', width: '50%'}, {align: 'start', key: 'value', width: '50%'}]"
              :items="dataSource.recommendation?.map(data => ({label: data.label, value: data.value}))">
            <template #headers></template>
            <template #bottom></template>
            <template #no-data></template>
          </v-data-table>
          <br/>
        </div>
        <div v-if="dataSource.externalId && dataSource.importSource">
          <h3>Imported from data source</h3>
          <p>{{dataSource.importSource}} ({{dataSource.externalId}})</p>
          <br/>
        </div>
        <div v-if="dataSource.customer">
          <h3>Originally delivered to customer</h3>
          <p>{{customers.get(dataSource.customer)?.name || 'You do not have access to this customer.'}}</p>
          <br/>
        </div>
      </v-card-text>
    </v-sheet>
  </v-card>
</template>

<script>
import {defineComponent} from 'vue';
import { VueToPrint } from "vue-to-print";
import axios from "axios";

export default defineComponent({
  name: 'LandscapingDataSourceDetailsCard',
  props: {
    dataSource: Object,
    currentVersion: Number,
    delta: Object,
    failure: String,
    customers: Map
  },
  components: {
    VueToPrint
  },
  data() {
    return {
      internalFailure: undefined
    }
  },
  methods: {
    hasAccessRight(accessRight) {
      return this.$store.getters.stateUser?.access_rights?.includes(accessRight);
    },
    openFile(id, file, contentType) {
      axios.get('/data-landscaping/data-source-file',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob',
            params: {
              data_source_id: id,
              file_name: file,
              content_type: contentType
            }
          })
          .then((response) => {
            this.internalFailure = undefined
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          }).catch(err => {
            this.internalFailure = 'Failed to load file!'
            console.log(err);
          });
    },
    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '';
      }
      return new Date(timestamp).toLocaleString();
    },
    computeValuesChangedDeltaClass(delta, fieldName) {
      if (delta['values_changed'] && Object.keys(delta['values_changed']).some(key =>
              key === 'root[\'' + fieldName + '\']') ||
          delta['type_changes'] && Object.keys(delta['type_changes']).some(key =>
              key === 'root[\'' + fieldName + '\']')) {
        return 'delta-changed'
      }
      return ''
    },
    computeFilesAddedClass(delta, file) {
      if (delta['iterable_item_added'] && Object.entries(delta['iterable_item_added']).some(([key, value]) =>
          key.startsWith('root[\'files\']') && (value.filename === file.filename) && (value.contentType === file.contentType))) {
        return 'delta-added'
      }
      return ''
    },
    computeFilesRemoved(delta) {
      return ((delta['iterable_item_removed'] && Object.entries(delta['iterable_item_removed'])) || []).filter(([key, ]) =>
          key.startsWith('root[\'files\']')).map(([, value]) => value)
    },
    computeValuesAddedClass(delta, fieldName, fieldValue) {
      if (delta['iterable_item_added'] && Object.entries(delta['iterable_item_added']).some(([key, value]) =>
          key.startsWith('root[\'' + fieldName + '\']') && (value === fieldValue))) {
        return 'delta-added'
      }
      return ''
    },
    computeValuesRemoved(delta, fieldName) {
      return ((delta['iterable_item_removed'] && Object.entries(delta['iterable_item_removed'])) || []).filter(([key, ]) =>
          key.startsWith('root[\'' + fieldName + '\']')).map(([, value]) => value)
    },
    computeSectionDeltaClass(delta, fieldName, sectionIndex) {
      if (delta['iterable_item_added'] && Object.keys(delta['iterable_item_added']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + ']')) {
        return 'delta-added'
      }
      if (delta['values_changed'] && Object.keys(delta['values_changed']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + '][\'title\']')) {
        return 'delta-changed'
      }
      return ''
    },
    computeSectionsRemoved(delta, fieldName) {
      return ((delta['iterable_item_removed'] && Object.entries(delta['iterable_item_removed'])) || []).filter(([key, value]) =>
          key.startsWith('root[\'' + fieldName + '\']') && value['items'] && value['title']).map(([, value]) => value)
    },
    computeLabelDeltaClass(delta, fieldName, sectionIndex, itemIndex) {
      // Completely new section, all item labels considered added
      if (delta['iterable_item_added'] && Object.keys(delta['iterable_item_added']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + ']')) {
        return 'delta-added'
      }
      if (delta['iterable_item_added'] && Object.keys(delta['iterable_item_added']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + '][\'items\'][' + itemIndex + ']')) {
        return 'delta-added'
      }
      if (delta['values_changed'] && Object.keys(delta['values_changed']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + '][\'items\'][' + itemIndex + '][\'label\']')) {
        return 'delta-changed'
      }
      return ''
    },
    computeValueDeltaClass(delta, fieldName, sectionIndex, itemIndex) {
      // Completely new section, all item values considered added
      if (delta['iterable_item_added'] && Object.keys(delta['iterable_item_added']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + ']')) {
        return 'delta-added'
      }
      if (delta['iterable_item_added'] && Object.keys(delta['iterable_item_added']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + '][\'items\'][' + itemIndex + ']')) {
        return 'delta-added'
      }
      if (delta['values_changed'] && Object.keys(delta['values_changed']).some(key =>
          key === 'root[\'' + fieldName + '\'][' + sectionIndex + '][\'items\'][' + itemIndex + '][\'value\']')) {
        return 'delta-changed'
      }
      return ''
    },
    computeLabelValuesRemoved(delta, fieldName, sectionIndex) {
      return ((delta['iterable_item_removed'] && Object.entries(delta['iterable_item_removed'])) || []).filter(([key, ]) =>
          key.startsWith('root[\'' + fieldName + '\'][' + sectionIndex + '][\'items\']')).map(([, value]) => value)
    }
  }
});
</script>

<style lang="scss" scoped>
.error {
  color: indianred;
}
.delta-changed {
  background: goldenrod;
}
.delta-added {
  background: mediumaquamarine;
}
.delta-removed {
  background: lightcoral;
}
.delta-removed-disabled {
  background: rgba(240, 128, 128, 0.12);
}
</style>

